import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HelpCircle, X, LayoutGrid, Users, Info, Rocket, Target, ClipboardList, AlertTriangle, Settings2, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../../utils/cn";
import { laneColors } from "../../utils/constants";
import { Epic, User } from "../../types";

interface MatrixViewProps {
    epics: Epic[];
    users?: User[];
    selectedId: string | null;
    onSelect: (id: string) => void;
    getProjectName?: (id?: string) => string;
}

type ViewMode = 'score' | 'avatar';
type JitterMode = 'scatter' | 'grid';

/**
 * MatrixView 2.1 - Enhanced Layout
 * - Collapsible Controls (Hidden by default)
 * - Fixed position "HUD" Detail Card (No cropping)
 * - Full-width dynamic chart area
 */
export function MatrixView({ epics, users = [], selectedId, onSelect, getProjectName }: MatrixViewProps) {
    const [showControls, setShowControls] = useState(false); // Default hidden as requested
    const [viewMode, setViewMode] = useState<ViewMode>('score');
    const [jitterMode, setJitterMode] = useState<JitterMode>('scatter');
    const [hoveredQuad, setHoveredQuad] = useState<string | null>(null);
    const [selectedQuad, setSelectedQuad] = useState<string | null>(null);
    const [hoveredEpic, setHoveredEpic] = useState<string | null>(null);

    // Fixed scale values
    const EFFORT_MIN = 1;
    const EFFORT_MAX = 5;
    const COD_MIN = 0;
    const COD_MAX = 100;

    // Filter Logic
    const activeQuad = hoveredQuad || selectedQuad;

    // Calculate quadrant counts
    const quadrantCounts = useMemo(() => {
        const counts = { q1: 0, q2: 0, q3: 0, q4: 0 };
        epics.forEach(e => {
            const effort = e.effort || 1;
            const valForY = (e as any).cod !== undefined ? (e as any).cod : (e.business || 0);
            const isHighValue = valForY > 50;
            const isHighEffort = effort > 3;

            if (isHighValue && !isHighEffort) counts.q1++;
            else if (isHighValue && isHighEffort) counts.q2++;
            else if (!isHighValue && !isHighEffort) counts.q3++;
            else counts.q4++;
        });
        return counts;
    }, [epics]);

    // Deterministic jitter
    const getJitter = (id: string, axis: 'x' | 'y') => {
        if (jitterMode === 'grid') return 0;
        const hash = id.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
        return axis === 'x'
            ? ((hash % 100) / 100 - 0.5) * 8
            : ((hash * 7 % 100) / 100 - 0.5) * 8;
    };

    // Helper to find user by various ID fields with flexible matching
    const getUser = (userId?: string) => {
        if (!userId) return undefined;

        const search = userId.toLowerCase().trim();

        return users.find(u => {
            // Priority 1: Exact ID matches
            if (u.id === userId || u.localAccountId === userId) return true;

            // Priority 2: Name matches (case-insensitive)
            const name = (u.name || '').toLowerCase();
            const displayName = (u.displayName || '').toLowerCase();

            if (name === search || displayName === search) return true;

            // Priority 3: Fuzzy name matching (First Last vs Last, First)
            // If the search term is part of the name or vice versa
            if (displayName.includes(search) || search.includes(displayName)) return true;

            // Handle "Last, First" vs "First Last"
            if (search.includes(',')) {
                // search is "Last, First", try to match "First Last" in displayName
                const [last, first] = search.split(',').map(s => s.trim());
                if (first && last && displayName.includes(first) && displayName.includes(last)) return true;
            } else {
                // search is "First Last", try to match "Last, First" in displayName
                const parts = search.split(' ');
                if (parts.length >= 2) {
                    const allPartsMatch = parts.every(part => displayName.includes(part));
                    if (allPartsMatch) return true;
                }
            }

            return false;
        });
    };

    // Get active item details
    const activeItem = useMemo(() => {
        const id = hoveredEpic || selectedId;
        if (!id) return null;
        return epics.find(e => e.id === id);
    }, [hoveredEpic, selectedId, epics]);

    const activeUser = activeItem ? getUser(activeItem.owner || activeItem.userId) : null;

    return (
        <div className="p-3 md:p-6 h-full flex flex-col md:flex-row gap-4 md:gap-6 relative overflow-hidden bg-siemens-stone/30 dark:bg-slate-950/30">

            {/* Toggle Controls Button (Floating or Sidebar) */}
            <AnimatePresence>
                {!showControls && (
                    <motion.button
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        onClick={() => setShowControls(true)}
                        className="absolute left-4 top-4 z-20 p-2 bg-white dark:bg-slate-800 rounded-lg shadow-md border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                        title="Show Controls"
                    >
                        <Settings2 className="w-5 h-5 text-siemens-petrol" />
                    </motion.button>
                )}
            </AnimatePresence>

            {/* Sidebar Controls & Legend (Collapsible) */}
            <AnimatePresence mode="wait">
                {showControls && (
                    <motion.div
                        initial={{ width: 0, opacity: 0, marginLeft: -20 }}
                        animate={{ width: "auto", opacity: 1, marginLeft: 0 }}
                        exit={{ width: 0, opacity: 0, marginLeft: -20 }}
                        className="flex-shrink-0 flex flex-col gap-4 z-20 relative h-full"
                    >
                        <div className="w-64 flex flex-col h-full">
                            {/* Close Button placed absolutely or inside header */}
                            <div className="w-full bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl border border-white/20 dark:border-white/10 shadow-xl overflow-hidden ring-1 ring-black/5">
                                <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-center relative">
                                    <div className="flex items-center gap-2">
                                        <LayoutGrid className="w-4 h-4 text-siemens-petrol" />
                                        <h3 className="text-xs font-bold uppercase tracking-wider text-siemens-deep-blue dark:text-white">Controls</h3>
                                    </div>
                                    <button
                                        onClick={() => setShowControls(false)}
                                        className="absolute right-4 p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors"
                                    >
                                        <X className="w-4 h-4 text-slate-400" />
                                    </button>
                                </div>

                                <div className="p-4 space-y-6">
                                    {/* View Mode Toggles */}
                                    <div className="space-y-2">
                                        <div className="text-[10px] font-bold uppercase text-slate-400">Display Mode</div>
                                        <div className="grid grid-cols-2 gap-2 bg-slate-100 dark:bg-slate-800 p-1 rounded-lg">
                                            <button
                                                onClick={() => setViewMode('score')}
                                                className={cn(
                                                    "flex items-center justify-center gap-2 py-1.5 px-2 rounded-md text-[10px] font-bold transition-all",
                                                    viewMode === 'score' ? "bg-white dark:bg-slate-700 shadow text-siemens-petrol" : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                                                )}
                                            >
                                                <Target className="w-3 h-3" /> Score
                                            </button>
                                            <button
                                                onClick={() => setViewMode('avatar')}
                                                className={cn(
                                                    "flex items-center justify-center gap-2 py-1.5 px-2 rounded-md text-[10px] font-bold transition-all",
                                                    viewMode === 'avatar' ? "bg-white dark:bg-slate-700 shadow text-siemens-petrol" : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                                                )}
                                            >
                                                <Users className="w-3 h-3" /> Owner
                                            </button>
                                        </div>
                                    </div>

                                    {/* Interactive Quadrants */}
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <div className="text-[10px] font-bold uppercase text-slate-400">Quadrants</div>
                                            <div className="text-[9px] text-slate-400 italic">Click to filter</div>
                                        </div>
                                        <div className="space-y-2">
                                            {[
                                                { id: 'q1', icon: Rocket, label: 'Quick Wins', desc: 'High Value, Low Effort', color: 'emerald', bg: 'bg-emerald-500' },
                                                { id: 'q2', icon: Target, label: 'Strategic', desc: 'High Value, High Effort', color: 'amber', bg: 'bg-amber-500' },
                                                { id: 'q3', icon: ClipboardList, label: 'Fill-Ins', desc: 'Low Value, Low Effort', color: 'slate', bg: 'bg-slate-500' },
                                                { id: 'q4', icon: AlertTriangle, label: 'Money Pit', desc: 'Low Value, High Effort', color: 'rose', bg: 'bg-rose-500' },
                                            ].map(q => {
                                                const isActive = activeQuad === q.id;
                                                const isSelected = selectedQuad === q.id;
                                                const count = quadrantCounts[q.id as keyof typeof quadrantCounts];

                                                return (
                                                    <button
                                                        key={q.id}
                                                        onClick={() => setSelectedQuad(prev => prev === q.id ? null : q.id)}
                                                        onMouseEnter={() => setHoveredQuad(q.id)}
                                                        onMouseLeave={() => setHoveredQuad(null)}
                                                        className={cn(
                                                            "w-full text-left group flex items-center gap-3 p-2 rounded-xl border transition-all cursor-pointer relative overflow-hidden",
                                                            isActive
                                                                ? "bg-white dark:bg-slate-800 shadow-md scale-105 border-slate-200 dark:border-slate-700"
                                                                : "border-transparent hover:bg-white/50 dark:hover:bg-slate-800/50",
                                                            isSelected && "ring-2 ring-siemens-petrol dark:ring-siemens-petrol-light"
                                                        )}
                                                    >
                                                        <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center text-white shadow-sm transition-transform", q.bg, isActive && "scale-110")}>
                                                            <q.icon className="w-4 h-4" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center justify-between">
                                                                <div className={cn("text-xs font-bold", `text-${q.color}-600 dark:text-${q.color}-400`)}>{q.label}</div>
                                                                <div className={cn(
                                                                    "px-1.5 py-0.5 rounded-full text-[9px] font-black",
                                                                    isActive
                                                                        ? `bg-${q.color}-100 text-${q.color}-700 dark:bg-${q.color}-900/50 dark:text-${q.color}-300`
                                                                        : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400"
                                                                )}>
                                                                    {count}
                                                                </div>
                                                            </div>
                                                            <div className="text-[10px] text-slate-400">{q.desc}</div>
                                                        </div>
                                                        {/* Active Indicator */}
                                                        {isActive && (
                                                            <motion.div
                                                                layoutId="active-indicator"
                                                                className={cn("absolute right-2 w-1.5 h-1.5 rounded-full", q.bg)}
                                                            />
                                                        )}
                                                    </button>
                                                )
                                            })}
                                        </div>
                                    </div>

                                    {/* Stats Footer */}
                                    <div className="pt-4 border-t border-slate-100 dark:border-slate-800 flex justify-between items-center">
                                        <span className="text-[10px] font-bold uppercase text-slate-400">Total Items</span>
                                        <span className="text-sm font-black text-siemens-deep-blue dark:text-white">{epics.length}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Main Matrix Area - Full Width/Height Dynamic */}
            <div className="flex-1 relative min-h-[400px] flex flex-col p-2 md:p-6 bg-white/40 dark:bg-slate-900/40 rounded-3xl border border-white/20 shadow-inner backdrop-blur-sm overflow-hidden">

                {/* Top Section: Y-Axis Legend + Grid */}
                <div className="flex-1 flex gap-2 min-h-0 relative">
                    {/* Y-Axis Label */}
                    <div className="w-6 flex flex-col items-center justify-center">
                        <div className="-rotate-90 whitespace-nowrap text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 flex items-center gap-2 fixed-center">
                            <span>Business Value</span>
                            <div className="w-8 h-0.5 bg-gradient-to-r from-transparent to-slate-400" />
                        </div>
                    </div>

                    {/* The Grid - Full remaining width */}
                    <div className="flex-1 relative w-full h-full bg-white rounded-2xl shadow-2xl dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden">

                        {/* Background Gradients (Quadrants) */}
                        <div className="absolute inset-0 grid grid-cols-2 grid-rows-2 pointer-events-none z-0">
                            <div className={cn("transition-opacity duration-300 relative pointer-events-none", activeQuad && activeQuad !== 'q1' ? "opacity-30" : "opacity-100")}>
                                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-transparent" />
                                <div className="absolute top-4 left-4 p-2 rounded-lg bg-emerald-500/10 backdrop-blur border border-emerald-500/20">
                                    <Rocket className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                                </div>
                            </div>
                            <div className={cn("transition-opacity duration-300 relative pointer-events-none", activeQuad && activeQuad !== 'q2' ? "opacity-30" : "opacity-100")}>
                                <div className="absolute inset-0 bg-gradient-to-bl from-amber-500/10 to-transparent" />
                                <div className="absolute top-4 right-4 p-2 rounded-lg bg-amber-500/10 backdrop-blur border border-amber-500/20">
                                    <Target className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                                </div>
                            </div>
                            <div className={cn("transition-opacity duration-300 relative pointer-events-none", activeQuad && activeQuad !== 'q3' ? "opacity-30" : "opacity-100")}>
                                <div className="absolute inset-0 bg-gradient-to-tr from-slate-500/10 to-transparent" />
                                <div className="absolute bottom-4 left-4 p-2 rounded-lg bg-slate-500/10 backdrop-blur border border-slate-500/20">
                                    <ClipboardList className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                                </div>
                            </div>
                            <div className={cn("transition-opacity duration-300 relative pointer-events-none", activeQuad && activeQuad !== 'q4' ? "opacity-30" : "opacity-100")}>
                                <div className="absolute inset-0 bg-gradient-to-tl from-rose-500/10 to-transparent" />
                                <div className="absolute bottom-4 right-4 p-2 rounded-lg bg-rose-500/10 backdrop-blur border border-rose-500/20">
                                    <AlertTriangle className="w-4 h-4 text-rose-600 dark:text-rose-400" />
                                </div>
                            </div>
                        </div>

                        {/* Dotted Lines */}
                        <div className="absolute inset-0 pointer-events-none">
                            <div className="absolute top-0 bottom-0 left-1/2 w-0.5 bg-dashed border-l-2 border-dashed border-slate-300 dark:border-slate-700 opacity-50" />
                            <div className="absolute left-0 right-0 top-1/2 h-0.5 bg-dashed border-t-2 border-dashed border-slate-300 dark:border-slate-700 opacity-50" />
                        </div>

                        {/* Item Bubbles */}
                        {epics.map(e => {
                            const effort = e.effort || 1;
                            const valForY = (e as any).cod !== undefined ? (e as any).cod : (e.business || 0);

                            const x = 10 + ((effort - EFFORT_MIN) / (EFFORT_MAX - EFFORT_MIN)) * 80 + getJitter(e.id, 'x');
                            const y = 10 + ((valForY - COD_MIN) / (COD_MAX - COD_MIN)) * 80 + getJitter(e.id, 'y');

                            const clampedX = Math.min(94, Math.max(6, x));
                            const clampedY = Math.min(94, Math.max(6, y));
                            const displayY = 100 - clampedY;

                            const isHighValue = valForY > 50;
                            const isHighEffort = effort > 3;
                            let itemQuad = '';
                            if (isHighValue && !isHighEffort) itemQuad = 'q1';
                            else if (isHighValue && isHighEffort) itemQuad = 'q2';
                            else if (!isHighValue && !isHighEffort) itemQuad = 'q3';
                            else itemQuad = 'q4';

                            const isDimmed = activeQuad && activeQuad !== itemQuad;
                            const isSelected = selectedId === e.id;

                            // Quadrant Colors matching the controls
                            const quadColors: Record<string, { bg: string, border: string, ring: string }> = {
                                q1: { bg: "bg-emerald-100 dark:bg-emerald-900/50", border: "border-emerald-500", ring: "ring-emerald-500" },
                                q2: { bg: "bg-amber-100 dark:bg-amber-900/50", border: "border-amber-500", ring: "ring-amber-500" },
                                q3: { bg: "bg-slate-100 dark:bg-slate-800", border: "border-slate-500", ring: "ring-slate-500" },
                                q4: { bg: "bg-rose-100 dark:bg-rose-900/50", border: "border-rose-500", ring: "ring-rose-500" }
                            };

                            const quadSolidColors: Record<string, string> = {
                                q1: "bg-emerald-600 dark:bg-emerald-500",
                                q2: "bg-amber-500 dark:bg-amber-400",
                                q3: "bg-slate-500 dark:bg-slate-400",
                                q4: "bg-rose-500 dark:bg-rose-400"
                            };

                            const colors = quadColors[itemQuad];

                            return (
                                <div
                                    key={e.id}
                                    className="absolute"
                                    style={{ left: `${clampedX}%`, top: `${displayY}%`, transform: 'translate(-50%, -50%)', zIndex: isSelected || hoveredEpic === e.id ? 50 : 10 }}
                                >
                                    <motion.button
                                        initial={{ scale: 0 }}
                                        animate={{
                                            scale: isSelected ? 1.5 : (isDimmed ? 0.8 : 1),
                                            opacity: isDimmed ? 0.3 : 1
                                        }}
                                        whileHover={{ scale: 1.3, zIndex: 100 }}
                                        onClick={() => onSelect(e.id)}
                                        onMouseEnter={() => setHoveredEpic(e.id)}
                                        onMouseLeave={() => setHoveredEpic(null)}
                                        className={cn(
                                            "w-10 h-10 rounded-full shadow-lg flex items-center justify-center transition-colors border-2",
                                            isSelected
                                                ? cn("ring-4 ring-offset-2 border-white dark:border-slate-800", colors.ring)
                                                : "border-white dark:border-slate-800",
                                            colors.bg,
                                            colors.border
                                        )}
                                    >
                                        {viewMode === 'score' ? (
                                            <span className="text-[10px] font-black leading-none text-slate-800 dark:text-white">
                                                {(e.wsjf || 0).toFixed(0)}
                                            </span>
                                        ) : (() => {
                                            // Get the first Service Owner for avatar display
                                            const soIds = Array.isArray(e.so) ? e.so : (e.so ? [e.so] : []);
                                            const primaryOwnerId = soIds[0];
                                            const ownerUser = primaryOwnerId ? getUser(primaryOwnerId) : null;
                                            const displayInitials = ownerUser?.displayName?.substring(0, 2) ||
                                                primaryOwnerId?.substring(0, 2) ||
                                                "??";

                                            return (
                                                <div className="w-full h-full rounded-full overflow-hidden bg-white/50">
                                                    {ownerUser?.photoUrl ? (
                                                        <img src={ownerUser.photoUrl} alt="Owner" className="w-full h-full object-cover" />
                                                    ) : (
                                                        <div className={cn("w-full h-full flex items-center justify-center text-white text-[10px] uppercase font-bold", quadSolidColors[itemQuad])}>
                                                            {displayInitials}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                    </motion.button>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* X-Axis Label */}
                <div className="flex justify-center pt-2">
                    <div className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 flex items-center gap-2">
                        <div className="w-8 h-0.5 bg-gradient-to-r from-transparent to-slate-400" />
                        <span>Effort Required</span>
                        <div className="w-8 h-0.5 bg-gradient-to-l from-transparent to-slate-400" />
                    </div>
                </div>
            </div>

            {/* Fixed HUD Detail Card (Right Side) - Outside the matrix grid to avoid cropping */}
            <AnimatePresence>
                {activeItem && (
                    <motion.div
                        initial={{ opacity: 0, x: 50, scale: 0.9 }}
                        animate={{ opacity: 1, x: 0, scale: 1 }}
                        exit={{ opacity: 0, x: 50, scale: 0.9 }}
                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                        className="absolute right-4 top-4 z-50 w-64 md:w-80 bg-white/90 dark:bg-slate-900/95 backdrop-blur-2xl rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden ring-1 ring-black/10 pointer-events-none"
                    >
                        {/* Header Color Bar */}
                        <div className={cn("h-2 w-full", laneColors[activeItem.lane]?.bg || "bg-slate-500")} />

                        <div className="p-4">
                            <div className="flex items-start justify-between gap-2 mb-2">
                                <div className="text-[10px] uppercase font-bold text-slate-400">{getProjectName?.(activeItem.projectId) || "Project"}</div>
                                <div className="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-[10px] font-bold text-slate-500 uppercase">
                                    {activeItem.lane}
                                </div>
                            </div>

                            <h3 className="text-lg font-bold text-siemens-deep-blue dark:text-white leading-tight mb-4">{activeItem.title}</h3>

                            {/* Stats */}
                            <div className="grid grid-cols-2 gap-3 mb-4">
                                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3 flex flex-col items-center justify-center border border-slate-100 dark:border-slate-800">
                                    <div className="text-[10px] uppercase text-slate-400 font-bold mb-1">WSJF Score</div>
                                    <div className="text-2xl font-black text-siemens-petrol">{(activeItem.wsjf || 0).toFixed(1)}</div>
                                </div>
                                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3 flex flex-col items-center justify-center border border-slate-100 dark:border-slate-800">
                                    <div className="text-[10px] uppercase text-slate-400 font-bold mb-1">Effort</div>
                                    <div className="text-2xl font-black text-rose-500">{activeItem.effort || 1}</div>
                                </div>
                            </div>

                            {/* Service Owners */}
                            <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-100 dark:border-slate-800 mb-2">
                                <div className="flex -space-x-2">
                                    {(Array.isArray(activeItem.so) && activeItem.so.length > 0) ? activeItem.so.filter(id => id && id.trim().length > 0).map((userId: string) => {
                                        const u = getUser(userId);
                                        return u?.photoUrl ? (
                                            <img key={userId} src={u.photoUrl} className="w-8 h-8 rounded-full ring-2 ring-white dark:ring-slate-700" title={u.displayName} />
                                        ) : (
                                            <div key={userId} className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center font-bold text-xs ring-2 ring-white dark:ring-slate-700" title={u?.displayName || userId}>
                                                {(u?.displayName || userId || "??").substring(0, 1).toUpperCase()}
                                            </div>
                                        );
                                    }) : (
                                        <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-300">
                                            <Users className="w-4 h-4" />
                                        </div>
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-[10px] uppercase font-bold text-slate-400">Service Owners</div>
                                    <div className="text-xs font-bold text-siemens-deep-blue dark:text-white truncate">
                                        {(Array.isArray(activeItem.so) && activeItem.so.length > 0)
                                            ? activeItem.so.filter(id => id && id.trim().length > 0).map(id => getUser(id)?.displayName || id).join(", ")
                                            : "Unassigned"}
                                    </div>
                                </div>
                            </div>

                            {/* Architects */}
                            <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-100 dark:border-slate-800">
                                <div className="flex -space-x-2">
                                    {(Array.isArray(activeItem.arq) && activeItem.arq.length > 0) ? activeItem.arq.filter(id => id && id.trim().length > 0).map((userId: string) => {
                                        const u = getUser(userId);
                                        return u?.photoUrl ? (
                                            <img key={userId} src={u.photoUrl} className="w-8 h-8 rounded-full ring-2 ring-white dark:ring-slate-700" title={u.displayName} />
                                        ) : (
                                            <div key={userId} className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center font-bold text-xs ring-2 ring-white dark:ring-slate-700" title={u?.displayName || userId}>
                                                {(u?.displayName || userId || "??").substring(0, 1).toUpperCase()}
                                            </div>
                                        );
                                    }) : (
                                        <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-300">
                                            <Settings2 className="w-4 h-4" />
                                        </div>
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-[10px] uppercase font-bold text-slate-400">Architects</div>
                                    <div className="text-xs font-bold text-siemens-deep-blue dark:text-white truncate">
                                        {(Array.isArray(activeItem.arq) && activeItem.arq.length > 0)
                                            ? activeItem.arq.filter(id => id && id.trim().length > 0).map(id => getUser(id)?.displayName || id).join(", ")
                                            : "Unassigned"}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export default MatrixView;
